<template>
    <div class="header" :class="{'mobile-search-active': mobileSearchOpen}">
        <div class="header-left h-full flex items-center" :class="{ 'sidebar-collapsed': isDesktopSidebarCollapsed }">
            <a href="#" class="mobile_btn" @click.prevent="sidebarTrigger()">
                <span class="material-symbols-outlined">menu</span>
            </a>
        </div>
        <div class="header-center">
            <div class="header-logo-wrapper" :class="{'logo-hidden': mobileSearchOpen}">
                <router-link to="/dashboard" class="header-logo">
                    <img src="/images/ForYellowLogo.png" alt="Brittany Palazzo">
                </router-link>
            </div>

            <div class="header-search header-search-mobile" :class="{'visible': mobileSearchOpen}" v-show="isMobile && mobileSearchOpen" role="search" aria-label="Global search">
                <span class="material-symbols-outlined search-icon">search</span>
            <input
                ref="searchInputMobile"
                type="text"
                placeholder="Search clients, events, billings..."
                class="search-input"
                v-model="searchQuery"
                @keyup="debouncedSearch"
                @keydown="handleSearchKeydown"
                @focus="onSearchFocus"
            >
            <button v-if="searchQuery" class="search-clear" @click="clearSearch">
                <span class="material-symbols-outlined">close</span>
            </button>
            <div class="search-dropdown" v-if="searchOpen" @click.stop>
                <div v-if="searchLoading" class="search-loading">
                    <span class="material-symbols-outlined search-spinner">progress_activity</span>
                    <span>Searching...</span>
                </div>
                <template v-else-if="searchResults && hasResults">
                    <div v-if="searchResults.clients.length" class="search-category">
                        <div class="search-category-header">
                            <span class="material-symbols-outlined">group</span>
                            Clients
                        </div>
                        <a
                            v-for="(item, idx) in searchResults.clients"
                            :key="'c'+item.id"
                            class="search-result-item"
                            :class="{ 'active': activeResultIndex === flatResults.findIndex(r => r.id === item.id && r.type === 'client') }"
                            href="#"
                            @click.prevent="selectResult(item.route)"
                        >
                            <span class="result-name">{{ item.name }}</span>
                            <span class="result-sub">{{ item.email }}</span>
                        </a>
                    </div>
                    <div v-if="searchResults.events.length" class="search-category">
                        <div class="search-category-header">
                            <span class="material-symbols-outlined">event</span>
                            Events
                        </div>
                        <a
                            v-for="item in searchResults.events"
                            :key="'e'+item.id"
                            class="search-result-item"
                            :class="{ 'active': activeResultIndex === flatResults.findIndex(r => r.id === item.id && r.type === 'event') }"
                            href="#"
                            @click.prevent="selectResult(item.route)"
                        >
                            <span class="result-name">{{ item.name }}</span>
                            <span class="result-sub">{{ item.event_date }}</span>
                        </a>
                    </div>
                    <div v-if="searchResults.billings.length" class="search-category">
                        <div class="search-category-header">
                            <span class="material-symbols-outlined">payments</span>
                            Billings
                        </div>
                        <a
                            v-for="item in searchResults.billings"
                            :key="'b'+item.id"
                            class="search-result-item"
                            :class="{ 'active': activeResultIndex === flatResults.findIndex(r => r.id === item.id && r.type === 'billing') }"
                            href="#"
                            @click.prevent="selectResult(item.route)"
                        >
                            <span class="result-name">{{ item.name }}</span>
                            <span class="result-sub">{{ item.status }}</span>
                        </a>
                    </div>
                    <div v-if="searchResults.payments && searchResults.payments.length" class="search-category">
                        <div class="search-category-header">
                            <span class="material-symbols-outlined">receipt_long</span>
                            Payments
                        </div>
                        <a
                            v-for="item in searchResults.payments"
                            :key="'p'+item.id"
                            class="search-result-item"
                            :class="{ 'active': activeResultIndex === flatResults.findIndex(r => r.id === item.id && r.type === 'payment') }"
                            href="#"
                            @click.prevent="selectResult(item.route)"
                        >
                            <span class="result-name">₱{{ formatAmount(item.amount) }} — {{ item.payment_method }}</span>
                            <span class="result-sub">{{ item.payment_status }} · {{ item.payment_date || item.created_at }}</span>
                        </a>
                    </div>
                    <div v-if="searchResults.users && searchResults.users.length" class="search-category">
                        <div class="search-category-header">
                            <span class="material-symbols-outlined">person</span>
                            Users
                        </div>
                        <a
                            v-for="item in searchResults.users"
                            :key="'u'+item.id"
                            class="search-result-item"
                            :class="{ 'active': activeResultIndex === flatResults.findIndex(r => r.id === item.id && r.type === 'user') }"
                            href="#"
                            @click.prevent="selectResult(item.route)"
                        >
                            <span class="result-name">{{ item.name }}</span>
                            <span class="result-sub">{{ item.email }}</span>
                        </a>
                    </div>
                </template>
                <div v-else-if="searchResults && !hasResults" class="search-empty">
                    <span class="material-symbols-outlined">search_off</span>
                    <span>No results found for "{{ searchQuery }}"</span>
                </div>
            </div>
        </div>
        </div>

        <div class="header-right">
            <button class="header-icon-btn mobile-search-btn" v-show="isMobile && !mobileSearchOpen" @click="openMobileSearch" aria-label="Open search" aria-expanded="false">
                <span class="material-symbols-outlined">search</span>
            </button>
            <button class="header-icon-btn" v-show="isMobile && mobileSearchOpen" @click="closeMobileSearch" aria-label="Close search" aria-expanded="true">
                <span class="material-symbols-outlined">arrow_back</span>
            </button>
            <div class="header-search" :class="{ 'mobile-search-open': mobileSearchOpen }" v-if="!isMobile">
                <span class="material-symbols-outlined search-icon">search</span>
                <input
                    ref="searchInput"
                    type="text"
                    placeholder="Search clients, events, billings..."
                    class="search-input"
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    @keydown="handleSearchKeydown"
                    @focus="onSearchFocus"
                >
                <button v-if="searchQuery" class="search-clear" @click="clearSearch">
                    <span class="material-symbols-outlined">close</span>
                </button>
                <div class="search-dropdown" v-if="searchOpen" @click.stop>
                    <div v-if="searchLoading" class="search-loading">
                        <span class="material-symbols-outlined search-spinner">progress_activity</span>
                        <span>Searching...</span>
                    </div>
                    <template v-else-if="searchResults && hasResults">
                        <div v-if="searchResults.clients.length" class="search-category">
                            <div class="search-category-header">
                                <span class="material-symbols-outlined">group</span>
                                Clients
                            </div>
                            <a
                                v-for="(item, idx) in searchResults.clients"
                                :key="'c'+item.id"
                                class="search-result-item"
                                :class="{ 'active': activeResultIndex === flatResults.findIndex(r => r.id === item.id && r.type === 'client') }"
                                href="#"
                                @click.prevent="selectResult(item.route)"
                            >
                                <span class="result-name">{{ item.name }}</span>
                                <span class="result-sub">{{ item.email }}</span>
                            </a>
                        </div>
                        <div v-if="searchResults.events.length" class="search-category">
                            <div class="search-category-header">
                                <span class="material-symbols-outlined">event</span>
                                Events
                            </div>
                            <a
                                v-for="item in searchResults.events"
                                :key="'e'+item.id"
                                class="search-result-item"
                                :class="{ 'active': activeResultIndex === flatResults.findIndex(r => r.id === item.id && r.type === 'event') }"
                                href="#"
                                @click.prevent="selectResult(item.route)"
                            >
                                <span class="result-name">{{ item.name }}</span>
                                <span class="result-sub">{{ item.event_date }}</span>
                            </a>
                        </div>
                        <div v-if="searchResults.billings.length" class="search-category">
                            <div class="search-category-header">
                                <span class="material-symbols-outlined">payments</span>
                                Billings
                            </div>
                            <a
                                v-for="item in searchResults.billings"
                                :key="'b'+item.id"
                                class="search-result-item"
                                :class="{ 'active': activeResultIndex === flatResults.findIndex(r => r.id === item.id && r.type === 'billing') }"
                                href="#"
                                @click.prevent="selectResult(item.route)"
                            >
                                <span class="result-name">{{ item.name }}</span>
                                <span class="result-sub">{{ item.status }}</span>
                            </a>
                        </div>
                        <div v-if="searchResults.payments && searchResults.payments.length" class="search-category">
                            <div class="search-category-header">
                                <span class="material-symbols-outlined">receipt_long</span>
                                Payments
                            </div>
                            <a
                                v-for="item in searchResults.payments"
                                :key="'p'+item.id"
                                class="search-result-item"
                                :class="{ 'active': activeResultIndex === flatResults.findIndex(r => r.id === item.id && r.type === 'payment') }"
                                href="#"
                                @click.prevent="selectResult(item.route)"
                            >
                                <span class="result-name">₱{{ formatAmount(item.amount) }} — {{ item.payment_method }}</span>
                                <span class="result-sub">{{ item.payment_status }} · {{ item.payment_date || item.created_at }}</span>
                            </a>
                        </div>
                        <div v-if="searchResults.users && searchResults.users.length" class="search-category">
                            <div class="search-category-header">
                                <span class="material-symbols-outlined">person</span>
                                Users
                            </div>
                            <a
                                v-for="item in searchResults.users"
                                :key="'u'+item.id"
                                class="search-result-item"
                                :class="{ 'active': activeResultIndex === flatResults.findIndex(r => r.id === item.id && r.type === 'user') }"
                                href="#"
                                @click.prevent="selectResult(item.route)"
                            >
                                <span class="result-name">{{ item.name }}</span>
                                <span class="result-sub">{{ item.email }}</span>
                            </a>
                        </div>
                    </template>
                    <div v-else-if="searchResults && !hasResults" class="search-empty">
                        <span class="material-symbols-outlined">search_off</span>
                        <span>No results found for "{{ searchQuery }}"</span>
                    </div>
                </div>
            </div>
            <div class="notification-wrapper" v-if="!isMobile">
                <button class="header-icon-btn" @click="toggleNotifications" :class="{ 'active': notificationOpen }">
                    <span class="material-symbols-outlined">notifications</span>
                    <span v-if="unreadCount > 0" class="notification-badge">{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
                </button>
                <div class="notification-dropdown" v-if="notificationOpen" @click.stop>
                    <div class="notification-header">
                        <span>Notifications</span>
                        <button v-if="unreadCount > 0" @click="markAllAsRead" class="mark-all-read">Mark all read</button>
                    </div>
                    <div class="notification-list" v-if="notifications.length > 0">
                        <div
                            v-for="notification in notifications"
                            :key="notification.id"
                            class="notification-item"
                            :class="{ 'unread': !notification.read_at }"
                            @click="handleNotificationClick(notification)"
                        >
                            <div class="notification-icon" :class="notification.data?.type">
                                <span class="material-symbols-outlined">
                                    {{ notification.data?.type === 'payment_received' ? 'payments' : 'notifications' }}
                                </span>
                            </div>
                            <div class="notification-content">
                                <div class="notification-title">{{ notification.data?.title || 'Notification' }}</div>
                                <div class="notification-message">{{ notification.data?.message }}</div>
                                <div class="notification-time">{{ formatTime(notification.created_at) }}</div>
                            </div>
                        </div>
                    </div>
                    <div class="notification-empty" v-else>
                        <span class="material-symbols-outlined">inbox</span>
                        <span>No notifications</span>
                    </div>
                </div>
            </div>
            <div class="header-profile" @click="profileMenuOpen = !profileMenuOpen">
                <div class="profile-avatar">
                    {{ userInitials }}
                </div>
                <div class="profile-info" v-if="!isMobile">
                    <span class="profile-name">{{ userName }}</span>
                    <span class="profile-role">{{ userRole }}</span>
                </div>
                <span class="material-symbols-outlined profile-arrow" v-if="!isMobile">expand_more</span>

                <div class="profile-dropdown" v-if="profileMenuOpen" @click.stop>
                    <div class="dropdown-user-info" v-if="isMobile">
                        <span class="dropdown-user-name">{{ userName }}</span>
                        <span class="dropdown-user-role">{{ userRole }}</span>
                    </div>
                    <div class="dropdown-divider" v-if="isMobile"></div>
                    <a href="#" class="dropdown-item" @click.prevent="changePassword">
                        <span class="material-symbols-outlined">lock</span>
                        Change Password
                    </a>
                    <a href="#" class="dropdown-item" @click.prevent="openManual">
                        <span class="material-symbols-outlined">menu_book</span>
                        User Manual
                    </a>
                    <div class="dropdown-divider"></div>
                    <a href="#" class="dropdown-item" @click.prevent="logout">
                        <span class="material-symbols-outlined">logout</span>
                        Logout
                    </a>
                    <div class="dropdown-version">{{ version }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            profileMenuOpen: false,
            isMobile: window.innerWidth < 768,
            version: '',
            searchQuery: '',
            searchResults: null,
            searchLoading: false,
            searchOpen: false,
            searchTimer: null,
            activeResultIndex: -1,
            mobileSearchOpen: false,
            notificationOpen: false,
            notifications: [],
            unreadCount: 0,
            notificationPollTimer: null,
        }
    },
    computed: {
        userName() {
            const user = JSON.parse(localStorage.getItem('user') || '{}')
            return user.name || 'Admin'
        },
        userRole() {
            const role = parseInt(localStorage.getItem('role'))
            if (role === 1) return 'Administrator'
            if (role === 2) return 'Sales'
            if (role === 3) return 'Accounting'
            return 'Staff'
        },
        userInitials() {
            const name = this.userName
            return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        },
        hasResults() {
            if (!this.searchResults) return false
            return (
                (this.searchResults.clients && this.searchResults.clients.length > 0) ||
                (this.searchResults.events && this.searchResults.events.length > 0) ||
                (this.searchResults.billings && this.searchResults.billings.length > 0) ||
                (this.searchResults.payments && this.searchResults.payments.length > 0) ||
                (this.searchResults.users && this.searchResults.users.length > 0)
            )
        },
        flatResults() {
            let results = []
            if (this.searchResults) {
                if (this.searchResults.clients) {
                    this.searchResults.clients.forEach(r => results.push({ ...r, type: 'client' }))
                }
                if (this.searchResults.events) {
                    this.searchResults.events.forEach(r => results.push({ ...r, type: 'event' }))
                }
                if (this.searchResults.billings) {
                    this.searchResults.billings.forEach(r => results.push({ ...r, type: 'billing' }))
                }
                if (this.searchResults.payments) {
                    this.searchResults.payments.forEach(r => results.push({ ...r, type: 'payment' }))
                }
                if (this.searchResults.users) {
                    this.searchResults.users.forEach(r => results.push({ ...r, type: 'user' }))
                }
            }
            return results
        }
    },
    mounted() {
        window.addEventListener('resize', this.handleResize)
        document.addEventListener('click', this.closeDropdowns)
        this.fetchVersion()
        this.fetchNotifications()
        this.startNotificationPolling()
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize)
        document.removeEventListener('click', this.closeDropdowns)
        this.stopNotificationPolling()
    },
    methods: {
        handleResize() {
            this.isMobile = window.innerWidth < 768
            if (!this.isMobile) {
                this.mobileSearchOpen = false
            }
        },
        closeDropdowns(e) {
            if (!this.$el.querySelector('.header-profile')?.contains(e.target)) {
                this.profileMenuOpen = false
            }
            if (!this.$el.querySelector('.header-search')?.contains(e.target)) {
                this.searchOpen = false
            }
            if (!this.$el.querySelector('.notification-wrapper')?.contains(e.target)) {
                this.notificationOpen = false
            }
            if (this.isMobile && this.mobileSearchOpen) {
                const mobileSearch = this.$el.querySelector('.header-search-mobile')
                const mobileBtn = this.$el.querySelector('.mobile-search-btn')
                if ((!mobileSearch || !mobileSearch.contains(e.target)) &&
                    (!mobileBtn || !mobileBtn.contains(e.target))) {
                    this.closeMobileSearch()
                }
            }
        },
        fetchVersion() {
            fetch('/version.json')
                .then(response => response.json())
                .then(data => { this.version = data.version })
                .catch(() => {})
        },
        changePassword() {
            this.profileMenuOpen = false
            this.$router.push('/changepassword')
        },
        openManual() {
            this.profileMenuOpen = false
            this.$emit('open-user-manual')
        },
        logout() {
            this.profileMenuOpen = false
            axios.post('/api/logout', {})
                .then(() => {
                    this.clearAuth()
                })
                .catch(() => {
                    this.clearAuth()
                })
        },
        clearAuth() {
            axios.defaults.headers.common['Authorization'] = ""
            localStorage.removeItem("vuex")
            localStorage.removeItem("role")
            localStorage.removeItem("id")
            localStorage.removeItem("user")
            this.$store.commit('removeToken')
            this.$router.push({ path: "/" })
        },
        openMobileSearch() {
            this.mobileSearchOpen = true
            this.searchQuery = ''
            this.searchResults = null
            this.searchOpen = false
            this.activeResultIndex = -1
            const btn = this.$el?.querySelector('.header-icon-btn.mobile-search-btn')
            if (btn) {
                btn.setAttribute('aria-expanded', 'true')
            }
            this.$nextTick(() => {
                this.$nextTick(() => {
                    const input = this.$refs.searchInputMobile || this.$refs.searchInput
                    if (input) {
                        input.focus()
                    }
                })
            })
        },
        closeMobileSearch() {
            this.mobileSearchOpen = false
            this.searchQuery = ''
            this.searchResults = null
            this.searchOpen = false
            const btn = this.$el?.querySelector('.header-icon-btn.mobile-search-btn')
            if (btn) {
                btn.setAttribute('aria-expanded', 'false')
                btn.focus()
            }
        },
        debouncedSearch() {
            clearTimeout(this.searchTimer)
            if (!this.searchQuery || this.searchQuery.length < 2) {
                this.searchResults = null
                this.searchOpen = false
                this.activeResultIndex = -1
                return
            }
            this.searchTimer = setTimeout(() => {
                this.performSearch()
            }, 300)
        },
        performSearch() {
            this.searchLoading = true
            this.searchOpen = true
            this.activeResultIndex = -1
            axios.get('/api/global-search', { params: { keyword: this.searchQuery } })
                .then(response => {
                    this.searchResults = response.data
                    this.searchLoading = false
                })
                .catch(() => {
                    this.searchResults = null
                    this.searchLoading = false
                    this.searchOpen = false
                })
        },
        clearSearch() {
            this.searchQuery = ''
            this.searchResults = null
            this.searchOpen = false
            this.activeResultIndex = -1
            if (this.$refs.searchInput) {
                this.$refs.searchInput.focus()
            }
        },
        selectResult(route) {
            this.searchOpen = false
            this.searchQuery = ''
            this.searchResults = null
            this.activeResultIndex = -1
            if (this.isMobile) {
                this.mobileSearchOpen = false
            }
            this.$router.push(route)
        },
        onSearchFocus() {
            if (this.searchQuery && this.searchQuery.length >= 2) {
                this.searchOpen = true
            }
        },
        handleSearchKeydown(e) {
            if (!this.searchOpen || !this.flatResults.length) return

            if (e.key === 'ArrowDown') {
                e.preventDefault()
                this.activeResultIndex = Math.min(this.activeResultIndex + 1, this.flatResults.length - 1)
            } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                this.activeResultIndex = Math.max(this.activeResultIndex - 1, -1)
            } else if (e.key === 'Enter') {
                e.preventDefault()
                if (this.activeResultIndex >= 0 && this.activeResultIndex < this.flatResults.length) {
                    const result = this.flatResults[this.activeResultIndex]
                    this.selectResult(result.route)
                }
            } else if (e.key === 'Escape') {
                e.preventDefault()
                this.searchOpen = false
                if (this.isMobile) {
                    this.closeMobileSearch()
                }
            }
        },
        toggleNotifications() {
            this.notificationOpen = !this.notificationOpen
            if (this.notificationOpen) {
                this.fetchNotifications()
            }
        },
        fetchNotifications() {
            if (!localStorage.getItem('vuex')) return
            axios.get('/api/notifications')
                .then(response => {
                    this.notifications = response.data.data || []
                    this.unreadCount = response.data.meta?.unread_count || 0
                })
                .catch(() => {})
        },
        startNotificationPolling() {
            this.notificationPollTimer = setInterval(() => {
                if (this.notificationOpen && localStorage.getItem('vuex')) {
                    this.fetchNotifications()
                }
            }, 30000)
        },
        stopNotificationPolling() {
            if (this.notificationPollTimer) {
                clearInterval(this.notificationPollTimer)
            }
        },
        markAllAsRead() {
            axios.post('/api/notifications/mark-all-read')
                .then(() => {
                    this.notifications.forEach(n => n.read_at = new Date())
                    this.unreadCount = 0
                })
                .catch(() => {})
        },
        handleNotificationClick(notification) {
            const notificationId = notification.id;
            const billingId = notification.data?.billing_id;
            
            axios.delete(`/api/notifications/${notificationId}`)
                .then(() => {
                    this.notifications = this.notifications.filter(n => n.id !== notificationId)
                    this.unreadCount = Math.max(0, this.unreadCount - 1)
                })
                .catch(() => {})
                .finally(() => {
                    this.notificationOpen = false
                    if (billingId) {
                        this.$router.push(`/viewbillings/${billingId}`)
                    } else {
                        this.$router.push(`/billings`)
                    }
                })
        },
        formatTime(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            const now = new Date()
            const diff = now - date
            const minutes = Math.floor(diff / 60000)
            const hours = Math.floor(diff / 3600000)
            const days = Math.floor(diff / 86400000)
            if (minutes < 1) return 'Just now'
            if (minutes < 60) return `${minutes}m ago`
            if (hours < 24) return `${hours}h ago`
            if (days < 7) return `${days}d ago`
            return date.toLocaleDateString()
        },
        formatAmount(val) {
            const raw = String(val || '').replace('₱', '')
            const num = Number(raw)
            if (isNaN(num)) return '0.00'
            return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }
    },
    emits: ['open-user-manual'],
    props: {
        sidebarTrigger: Function,
        isDesktopSidebarCollapsed: {
            type: Boolean,
            default: false
        }
    }
}
</script>

<style scoped>
.header {
    display: flex;
    align-items: center;
    padding: 0 24px 0 0;
    height: var(--header-height, 80px);
    background: linear-gradient(135deg, #775928, #b08d57);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-shadow: 0px 4px 12px rgba(119, 89, 40, 0.25);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1002;
    gap: 24px;
}

.header-left {
    flex-shrink: 0;
    width: var(--sidebar-width, 288px);
    padding-right: 24px;
    transition: width 0.3s ease, padding-right 0.3s ease;
}

.header-left.sidebar-collapsed {
    width: auto;
    padding-right: 0;
}

.header-logo img {
    max-height: 48px;
    width: auto;
}

.header-center {
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    overflow: hidden;
}

.header-center > * {
    position: absolute;
    transition: opacity 0.25s ease, transform 0.25s ease;
}

.header-center .header-logo-wrapper {
    position: relative;
    opacity: 1;
    transform: translateX(0) scale(1);
}

.header-center .header-logo-wrapper.logo-hidden {
    opacity: 0;
    transform: translateX(-20px) scale(0.95);
    pointer-events: none;
}

.header-center .header-search-mobile {
    opacity: 0;
    transform: translateX(20px);
    pointer-events: none;
    flex: 1;
    width: 100%;
    margin: 0;
}

.header-center .header-search-mobile.visible {
    opacity: 1;
    transform: translateX(0);
    pointer-events: all;
}

.mobile_btn {
    display: none;
    color: rgba(255, 255, 255, 0.9);
    margin-right: 12px;
    cursor: pointer;
    padding: 4px;
    border-radius: 8px;
    transition: background 150ms ease;
    min-width: 44px;
    min-height: 44px;
    align-items: center;
    justify-content: center;
}

.mobile_btn:hover {
    background: rgba(255, 255, 255, 0.15);
}

.header-search {
    width: 300px;
    flex-shrink: 0;
    position: relative;
}

.search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, 0.6);
    font-size: 20px;
    z-index: 1;
    pointer-events: none;
}

.search-input {
    width: 100%;
    padding: 10px 40px 10px 44px;
    border-radius: 9999px;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
    font-family: 'Manrope', sans-serif;
    font-size: 0.875rem;
    outline: none;
    transition: all 150ms ease;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2);
    box-sizing: border-box;
}

.search-input:focus {
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4), 0 0 0 3px rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.2);
}

.search-input::placeholder {
    color: rgba(255, 255, 255, 0.55);
}

.search-clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 150ms ease;
}

.search-clear:hover {
    color: #ffffff;
}

.search-clear .material-symbols-outlined {
    font-size: 18px;
}

.search-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: var(--color-bg-card, #ffffff);
    border-radius: 12px;
    box-shadow: 0px 12px 32px rgba(52, 48, 43, 0.12);
    max-height: 400px;
    overflow-y: auto;
    z-index: 1100;
    padding: 8px 0;
}

.search-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px;
    color: var(--color-text-muted, #807569);
    font-family: 'Manrope', sans-serif;
    font-size: 0.875rem;
}

.search-spinner {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

.search-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px;
    color: var(--color-text-muted, #807569);
    font-family: 'Manrope', sans-serif;
    font-size: 0.875rem;
    text-align: center;
}

.search-empty .material-symbols-outlined {
    font-size: 32px;
    opacity: 0.5;
}

.search-category {
    padding: 4px 0;
}

.search-category-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px 4px;
    font-family: 'Manrope', sans-serif;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--color-text-muted, #807569);
}

.search-category-header .material-symbols-outlined {
    font-size: 14px;
}

.search-result-item {
    display: flex;
    flex-direction: column;
    padding: 8px 14px;
    text-decoration: none;
    transition: background 150ms ease;
    border-left: 3px solid transparent;
}

.search-result-item:hover,
.search-result-item.active {
    background: rgba(119, 89, 40, 0.06);
    border-left-color: var(--color-primary, #775928);
}

.result-name {
    font-family: 'Manrope', sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--color-text-primary, #1e1b17);
    line-height: 1.3;
}

.result-sub {
    font-family: 'Manrope', sans-serif;
    font-size: 0.75rem;
    color: var(--color-text-muted, #807569);
    line-height: 1.3;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    flex-shrink: 0;
}

.header-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    transition: all 150ms ease;
}

.header-icon-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
}

.header-icon-btn .material-symbols-outlined {
    font-size: 22px;
}

.header-profile {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 12px 6px 6px;
    border-radius: 9999px;
    cursor: pointer;
    transition: all 150ms ease;
    position: relative;
}

.header-profile:hover {
    background: rgba(255, 255, 255, 0.15);
}

.profile-avatar {
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    background: #ffffff;
    color: #775928;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 700;
    font-size: 0.8125rem;
    flex-shrink: 0;
}

.profile-info {
    display: flex;
    flex-direction: column;
}

.profile-name {
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    font-size: 0.8125rem;
    color: rgba(255, 255, 255, 0.95);
    line-height: 1.2;
}

.profile-role {
    font-family: 'Manrope', sans-serif;
    font-weight: 500;
    font-size: 0.6875rem;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.2;
}

.profile-arrow {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.65);
}

.profile-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    min-width: 220px;
    background: var(--color-bg-card, #ffffff);
    border-radius: 12px;
    box-shadow: 0px 12px 32px rgba(52, 48, 43, 0.12);
    padding: 8px;
    z-index: 1100;
}

.dropdown-user-info {
    padding: 8px 14px 4px;
    display: flex;
    flex-direction: column;
}

.dropdown-user-name {
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--color-text-primary, #1e1b17);
}

.dropdown-user-role {
    font-family: 'Manrope', sans-serif;
    font-size: 0.75rem;
    color: var(--color-text-muted, #807569);
}

.dropdown-divider {
    height: 1px;
    background: var(--color-outline-variant, #d1c5b6);
    margin: 6px 8px;
}

.profile-dropdown .dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 8px;
    color: var(--color-text-primary, #1e1b17);
    font-family: 'Manrope', sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    text-decoration: none;
    transition: all 150ms ease;
}

.profile-dropdown .dropdown-item:hover {
    background: rgba(119, 89, 40, 0.06);
    color: var(--color-primary, #775928);
}

.profile-dropdown .dropdown-item .material-symbols-outlined {
    font-size: 18px;
}

.dropdown-version {
    padding: 8px 14px 4px;
    color: var(--color-text-muted, #807569);
    font-family: 'Manrope', sans-serif;
    font-size: 0.6875rem;
}

/* Desktop: logo at left edge, aligned with sidebar content */
@media (min-width: 769px) {
    .header-left {
        width: 0;
        padding-right: 0;
    }

    .mobile_btn {
        display: none !important;
    }

    .header-center {
        justify-content: flex-start;
    }
}

/* Mobile: menu button visible, logo animated via header-center */
@media (max-width: 768px) {
    .header-left {
        width: auto;
        padding-right: 0;
        flex-shrink: 0;
    }

    .mobile_btn {
        display: flex;
    }

    .header-center {
        margin: 0 8px;
    }

    .header-logo img {
        max-height: 36px;
    }

    .header.mobile-search-active .header-left {
        flex-shrink: 0;
    }

    .header.mobile-search-active .header-center {
        position: static;
        flex: 1;
        margin: 0 4px;
        overflow: visible;
    }

    .header.mobile-search-active .header-center .header-logo-wrapper {
        display: none;
    }

    .header.mobile-search-active .header-center .header-search-mobile {
        position: static;
        opacity: 1;
        transform: none;
        pointer-events: all;
        margin: 0;
    }

    .header.mobile-search-active .header-right {
        flex-shrink: 0;
    }
}

.notification-wrapper {
    position: relative;
}

.notification-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    background: #ac3231;
    color: white;
    font-size: 10px;
    font-weight: 700;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    font-family: 'Manrope', sans-serif;
}

.notification-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 360px;
    max-width: calc(100vw - 32px);
    max-height: 480px;
    background: var(--color-bg-card, #ffffff);
    border-radius: 12px;
    box-shadow: 0px 12px 32px rgba(52, 48, 43, 0.12);
    z-index: 1100;
    overflow: hidden;
}

.notification-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid var(--color-outline-variant, #d1c5b6);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    font-size: 0.875rem;
    color: var(--color-text-primary, #1e1b17);
}

.mark-all-read {
    background: none;
    border: none;
    color: var(--color-primary, #775928);
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    font-family: 'Manrope', sans-serif;
}

.mark-all-read:hover {
    text-decoration: underline;
}

.notification-list {
    max-height: 400px;
    overflow-y: auto;
}

.notification-item {
    display: flex;
    gap: 12px;
    padding: 12px 16px;
    cursor: pointer;
    transition: background 150ms ease;
    border-bottom: 1px solid var(--color-outline-variant, #d1c5b6);
}

.notification-item:last-child {
    border-bottom: none;
}

.notification-item:hover {
    background: rgba(119, 89, 40, 0.04);
}

.notification-item.unread {
    background: rgba(119, 89, 40, 0.06);
}

.notification-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--color-surface-container-low, #fbf2eb);
}

.notification-icon.payment_received {
    background: rgba(60, 105, 43, 0.1);
    color: #3c692b;
}

.notification-icon .material-symbols-outlined {
    font-size: 20px;
}

.notification-content {
    flex: 1;
    min-width: 0;
}

.notification-title {
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    font-size: 0.8125rem;
    color: var(--color-text-primary, #1e1b17);
    margin-bottom: 2px;
}

.notification-message {
    font-family: 'Manrope', sans-serif;
    font-size: 0.75rem;
    color: var(--color-text-secondary, #4e453a);
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.notification-time {
    font-family: 'Manrope', sans-serif;
    font-size: 0.6875rem;
    color: var(--color-text-muted, #807569);
    margin-top: 4px;
}

.notification-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 40px 16px;
    color: var(--color-text-muted, #807569);
    font-family: 'Manrope', sans-serif;
    font-size: 0.875rem;
}

.notification-empty .material-symbols-outlined {
    font-size: 32px;
    opacity: 0.5;
}

.header-icon-btn.active {
    background: rgba(255, 255, 255, 0.2);
}

.header-search-mobile {
    flex: 1;
    max-width: none;
    width: auto;
    min-width: 0;
    margin: 0 8px;
}

@media (max-width: 768px) {
    .header-search-mobile .search-input {
        font-size: 16px;
    }
}
</style>
